import React, { Component } from 'react'
import { connect } from 'react-redux'
import { getId } from '@adalo/utils'
import { dataTypes } from '@adalo/constants'
import { toast } from 'react-toastify'

import { newAction } from 'utils/actions'
import { isRole } from 'utils/libraries'
import {
  copyActionChain,
  hasActionClipboard,
  pasteActionChain,
  remapActionChain,
  subscribe,
} from 'utils/actionClipboard'
import { getDatasourceType } from 'ducks/apps'
import { getDatasources } from 'ducks/apps/datasources'
import { openAccordion } from 'ducks/accordions'
import {
  selectObject,
  getComponent,
  getCurrentAppId,
} from 'ducks/editor/objects'

import ActionItem from 'components/Editor/Actions/Item'
import ActionsEmptyState from 'components/Editor/Actions/EmptyState'
import Button from 'components/Shared/Button'
import Tooltip from 'components/Shared/Tooltip'
import Icon, { IconButton } from 'components/Shared/Icon'

class ActionRow extends Component {
  static defaultProps = {
    actionsKey: 'actions',
  }

  state = {
    hasClipboard: hasActionClipboard(),
  }

  componentDidMount() {
    this.unsubscribe = subscribe(() => {
      this.setState({ hasClipboard: hasActionClipboard() })
    })
  }

  componentWillUnmount() {
    if (this.unsubscribe) this.unsubscribe()
  }

  handlePaste = () => {
    const {
      name,
      onChange,
      openAccordion,
      value,
      object,
      actionsKey,
      appId,
      targetDatasources,
    } = this.props
    const result = pasteActionChain()

    if (!result) return

    let { action: pastedAction, sourceAppId, sourceDatasources } = result

    // Remap references when pasting across apps
    if (sourceAppId && sourceAppId !== appId && sourceDatasources) {
      pastedAction = remapActionChain(
        pastedAction,
        sourceDatasources,
        targetDatasources
      )
    }

    // If there's an existing action chain, merge into it
    if (value) {
      const existingAction = object?.[actionsKey]?.[value.actionId]

      if (existingAction) {
        const merged = {
          ...existingAction,
          actions: [...(existingAction.actions || []), ...pastedAction.actions],
        }

        openAccordion('action-item', pastedAction.actions[0].id)
        onChange(null, { [value.actionId]: merged })
        toast.success('Actions pasted', {
          position: toast.POSITION.BOTTOM_LEFT,
        })

        return
      }
    }

    // No existing actions — create a new action ref
    const actionId = getId()
    openAccordion('action-item', pastedAction.actions[0].id)

    onChange(
      { [name]: { type: dataTypes.ACTION_REF, actionId } },
      { [actionId]: pastedAction }
    )
    toast.success('Actions pasted', {
      position: toast.POSITION.BOTTOM_LEFT,
    })
  }

  handleCopy = () => {
    const { value, object, actionsKey, appId, targetDatasources } = this.props

    if (!value) return

    const action = object?.[actionsKey]?.[value.actionId]

    if (!action) return

    copyActionChain(action, appId, targetDatasources)
    toast.success('Actions copied', {
      position: toast.POSITION.BOTTOM_LEFT,
    })
  }

  handleAdd = defaults => {
    const { name, onChange, openAccordion } = this.props
    const action = newAction(defaults)
    const actionId = getId()
    openAccordion('action-item', action.actions[0].id)

    onChange(
      { [name]: { type: dataTypes.ACTION_REF, actionId } },
      { [actionId]: action }
    )
  }

  handleUpdate = newValue => {
    const { value, onChange } = this.props

    if (!value || !newValue) {
      return
    }

    const { actionId } = value

    if (newValue.actions && newValue.actions.length === 0) {
      return this.handleRemove()
    }

    onChange(null, { [actionId]: newValue })
  }

  handleRemove = () => {
    const { name, value, onChange } = this.props

    if (!value) {
      return
    }

    onChange({ [name]: null }, { [value.actionId]: undefined })
  }

  render() {
    let {
      displayName,
      value,
      objectId,
      appId,
      componentId,
      object,
      datasourceType,
      role,
      reference,
      actionsKey,
      dummyActions,
      filterMenuOptions,
      omitContextualTables,
    } = this.props

    const { hasClipboard } = this.state

    reference = isRole(role, 'listItem') && reference

    const tooltip = (
      <a
        href="https://help.adalo.com/action-basics"
        target="_blank"
        rel="noreferrer"
      >
        Learn more.
      </a>
    )

    if (!value && !(dummyActions && dummyActions.length > 0)) {
      return (
        <div className="library-inspect-action">
          <div className="action-row-header">
            <p className="library-inspect-action-title">{displayName}</p>
            <Tooltip tooltip={tooltip} hideArrow small>
              <Icon type="help-small" />
            </Tooltip>
          </div>
          <ActionsEmptyState
            onCreate={this.handleAdd}
            appId={appId}
            componentId={componentId}
            objectId={objectId}
            datasourceType={datasourceType}
            reference={reference}
          >
            <Button type="button" icon="plus-small" listAddButton gray>
              Add Action
            </Button>
          </ActionsEmptyState>
          {hasClipboard && (
            <Button
              type="button"
              icon="copy"
              listAddButton
              gray
              onClick={this.handlePaste}
            >
              Paste Actions
            </Button>
          )}
        </div>
      )
    }

    const chainDummyAction = (dummyActions?.length ?? 0) > 0 ? dummyActions : undefined // prettier-ignore

    return (
      <div className="library-inspect-action">
        <div className="action-row-header">
          <p className="library-inspect-action-title">{displayName}</p>
          <div className="action-row-header-icons">
            {value && (
              <IconButton
                type="copy"
                small
                tooltip="Copy Actions"
                onClick={this.handleCopy}
              />
            )}
            <Tooltip tooltip={tooltip} hideArrow small>
              <Icon type="help-small" />
            </Tooltip>
          </div>
        </div>
        <ActionChain
          dummyActions={chainDummyAction}
          filterMenuOptions={filterMenuOptions}
          omitContextualTables={omitContextualTables}
          actionId={value && value.actionId}
          objectId={objectId}
          appId={appId}
          object={object}
          componentId={componentId}
          datasourceType={datasourceType}
          onCreateFirst={this.handleAdd}
          onUpdate={this.handleUpdate}
          onRemove={this.handleRemove}
          reference={reference}
          actionsKey={actionsKey}
        />
        {hasClipboard && (
          <Button
            type="button"
            icon="copy"
            listAddButton
            gray
            onClick={this.handlePaste}
          >
            Paste Actions
          </Button>
        )}
      </div>
    )
  }
}

const mapStateToProps = (state, { objectId }) => {
  const appId = getCurrentAppId(state)

  return {
    appId,
    componentId: getComponent(state, objectId)?.id,
    object: selectObject(state, objectId),
    datasourceType: getDatasourceType(state, appId),
    targetDatasources: getDatasources(state, appId),
  }
}

export default connect(mapStateToProps, { openAccordion })(ActionRow)

class ActionChain extends Component {
  getAction = () => {
    const { object, actionId, actionsKey } = this.props

    return object?.[actionsKey]?.[actionId]
  }

  render() {
    const {
      appId,
      componentId,
      objectId,
      actionId,
      datasourceType,
      onUpdate,
      onRemove,
      reference,
      dummyActions,
      onCreateFirst,
      filterMenuOptions,
      omitContextualTables,
    } = this.props

    return (
      <ActionItem
        appId={appId}
        componentId={componentId}
        objectId={objectId}
        id={actionId}
        action={this.getAction()}
        datasourceType={datasourceType}
        onUpdate={onUpdate}
        onRemove={onRemove}
        reference={reference}
        dummyActions={dummyActions}
        onCreateFirst={onCreateFirst}
        filterMenuOptions={filterMenuOptions}
        omitContextualTables={omitContextualTables}
      />
    )
  }
}
